// ===== 2. React Hook for Inspection Recommendations =====
// src/hooks/useInspectionRecommendations.ts

import { useState, useEffect } from 'react';
import { 
  AIInspectionRecommendationService, 
  InspectionRecommendationResponse 
} from '../../../../services/ai/aiInspectionRecommendationService';
import { httpRequest } from 'src/services/httpRequest';

export const useInspectionRecommendations = (
  selectedSubcode: any,
  applicationData: any,
  applicationID: string | number
) => {
  const [recommendations, setRecommendations] = useState<InspectionRecommendationResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedSubcode || !applicationData || !applicationID) {
      setRecommendations(null);
      return;
    }

    const fetchRecommendations = async () => {
      setLoading(true);
      setError(null);

      try {
        // First, fetch the subcode form data
        const subcodeFormResponse = await httpRequest(
          'GET', 
          `/construction/ucc_form_share_list?ApplicationID=${applicationID}&UCCFormName=${selectedSubcode.SubcodeName}`
        );

        let subcodeFormData = null;
        
        if (subcodeFormResponse?.status === 'success' && subcodeFormResponse.data) {
          // Check both SelfFilled and GuestFilled arrays
          const allForms = [
            ...(subcodeFormResponse.data.SelfFilled || []),
            ...(subcodeFormResponse.data.GuestFilled || [])
          ];
          
          if (allForms.length > 0) {
            // Get the most recent form or the first available
            const formData = allForms[0];
            subcodeFormData = formData.UCCSubcodeDetails || formData;
          }
        }

        // Get AI recommendations
        const aiService = new AIInspectionRecommendationService();
        const result = await aiService.getInspectionRecommendations(
          selectedSubcode.SubcodeName,
          applicationData,
          subcodeFormData
        );
        setRecommendations(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to get inspection recommendations');
      } finally {
        setLoading(false);
      }
    };

    // Small delay to avoid too many API calls
    const timeoutId = setTimeout(fetchRecommendations, 800);
    return () => clearTimeout(timeoutId);

  }, [selectedSubcode, applicationData, applicationID]);

  return { recommendations, loading, error };
};
